import { useState, useCallback, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import useRedirect from "@shared/hooks/useRedirect.web";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useTranslation } from "@shared/utils/internationalization";
import { WAFFLE_FLAG_ONE_CLICK_CHECKOUT } from "@shared/constants/waffle-flags";
import { PurchaseData } from "@shared/modules/payments/types";
import {
  EXPRESS_CHECKOUT_QUERY_PARAM,
  EXPRESS_CHECKOUT_INVOICE_ID_PARAM,
  EXPRESS_CHECKOUT_UUID1_PARAM,
  EXPRESS_CHECKOUT_UUID2_PARAM,
} from "@shared/modules/payments/constants/express-checkout";
import { BASKET_BRANDING_COOKIE } from "@shared/modules/branding/utils/basket-branding-cookie";

export type OverlayPhase = "hidden" | "processing";

export type ExpressCheckoutPurchaseState =
  | "idle"
  | "wallet_active"
  | "processing"
  | "complete";

interface UseExpressCheckoutOptions {
  eligible: boolean;
  venueId?: number;
  onPurchaseStateChange?: (state: ExpressCheckoutPurchaseState) => void;
}

interface UseExpressCheckoutReturn {
  isWalletReady: boolean;
  setIsWalletReady: (ready: boolean) => void;
  overlayPhase: OverlayPhase;
  isOverlayVisible: boolean;
  overlayMessage: string;
  isExpressCheckoutEnabled: boolean;
  visible: boolean;
  handlePaymentSuccess: (data: PurchaseData) => void;
  handleWalletClick: () => void;
  handleWalletCancel: () => void;
}

export function useExpressCheckout({
  eligible,
  venueId,
  onPurchaseStateChange,
}: UseExpressCheckoutOptions): UseExpressCheckoutReturn {
  const { t } = useTranslation("checkout");
  const [isWalletReady, setIsWalletReady] = useState(false);
  const [isWalletSessionActive, setIsWalletSessionActive] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>("hidden");
  const overlayLocked = useRef(false);
  const { redirect } = useRedirect();
  const { isProcessingPurchase, isPurchaseComplete } = usePaymentManager();
  const { isFlagActive } = useDjangoWaffle();

  const isExpressCheckoutEnabled = isFlagActive(
    WAFFLE_FLAG_ONE_CLICK_CHECKOUT,
    venueId
  );

  // Snapshot the branding cookie so we can restore it before redirecting.
  // postPurchase() calls emptyBasket() which clears this cookie before
  // onPaymentSuccess fires, so the checkout GSSP would otherwise see no
  // branding and fall back to the unbranded legacy template.
  const brandingCookieRef = useRef(Cookies.get(BASKET_BRANDING_COOKIE));

  useEffect(() => {
    if (!isProcessingPurchase) {
      const value = Cookies.get(BASKET_BRANDING_COOKIE);
      if (value) {
        brandingCookieRef.current = value;
      }
    }
  }, [isProcessingPurchase]);

  useEffect(() => {
    if (isProcessingPurchase && !overlayLocked.current) {
      overlayLocked.current = true;
      setOverlayPhase("processing");
    }
    if (!isProcessingPurchase && overlayLocked.current && !isPurchaseComplete) {
      overlayLocked.current = false;
      setOverlayPhase("hidden");
    }
  }, [isProcessingPurchase, isPurchaseComplete]);

  const isOverlayVisible = overlayPhase !== "hidden";

  useEffect(() => {
    if (isOverlayVisible) {
      onPurchaseStateChange?.("processing");
    } else if (isPurchaseComplete) {
      onPurchaseStateChange?.("complete");
    } else if (isWalletSessionActive) {
      onPurchaseStateChange?.("wallet_active");
    } else {
      onPurchaseStateChange?.("idle");
    }
  }, [
    isOverlayVisible,
    isPurchaseComplete,
    isWalletSessionActive,
    onPurchaseStateChange,
  ]);

  const handleWalletClick = useCallback((): void => {
    setIsWalletSessionActive(true);
    onPurchaseStateChange?.("wallet_active");
  }, [onPurchaseStateChange]);

  const handleWalletCancel = useCallback((): void => {
    setIsWalletSessionActive(false);
  }, []);

  const handlePaymentSuccess = useCallback(
    (data: PurchaseData) => {
      NotifierFactory.createNotifier().success(
        t("Payment successful", { ns: "common" })
      );
      // restore the branding cookie so the checkout GSSP sees the correct branding
      if (brandingCookieRef.current) {
        Cookies.set(BASKET_BRANDING_COOKIE, brandingCookieRef.current, {
          path: "/",
          sameSite: "lax",
          // edge case: so that going to checkout page directly doesnt load wrong branding after express purchase
          expires: new Date(Date.now() + 2 * 60 * 1000), // expire in 2 minutes after the purchase
        });
      }

      const params = new URLSearchParams();
      params.set(EXPRESS_CHECKOUT_QUERY_PARAM, "true");

      if (data.finalBasket?.invoice_id != null) {
        params.set(
          EXPRESS_CHECKOUT_INVOICE_ID_PARAM,
          String(data.finalBasket.invoice_id)
        );
      }
      if (data.finalBasket?.uuid) {
        params.set(EXPRESS_CHECKOUT_UUID1_PARAM, data.finalBasket.uuid);
      }
      if (data.finalBasket?.uuid2) {
        params.set(EXPRESS_CHECKOUT_UUID2_PARAM, data.finalBasket.uuid2);
      }

      redirect(`/checkout/?${params.toString()}`);
    },
    [redirect, t]
  );

  const visible = eligible && isWalletReady && isExpressCheckoutEnabled;

  const overlayMessage = t("Processing your order");

  return {
    isWalletReady,
    setIsWalletReady,
    overlayPhase,
    isOverlayVisible,
    overlayMessage,
    isExpressCheckoutEnabled,
    visible,
    handlePaymentSuccess,
    handleWalletClick,
    handleWalletCancel,
  };
}
